import noop from 'lodash/noop';
import {
  SDL_HEADER_SELECTOR,
  SDL_HEADER_SELECTOR_APC,
  SDL_REDESIGN_HEADER_SELECTOR,
  TAB_BAR_HEIGHT,
  scrollBehavior,
  SLASH,
  Protocol,
  EEvent,
  ONLINE_CTA_TYPES,
  CartVersion,
} from './constants';
import type { Locale, TScrollToComponentOptions } from './types/common';
import { TProductCtasLabels } from './types/labels';
import { RangeCtaClickEventParams } from './types/events';
import { defaultPageLoadDataParams, pageLoadDataToAnalytics } from './constants/events';

const isApc = Boolean(window.isApc);

const isBrowser = () => typeof window !== 'undefined';
export const isV3 = () => Boolean(window.isV3);
export const isPBS = () => Boolean(window.isPBS);
export const isIndexDBEnabled = () => Boolean(window.isIndexDBEnabled);
export const isPublicEnvironment = (isExecutedInBrowser = isBrowser): boolean => {
  if (!isExecutedInBrowser()) return false;

  if (window.isPublicPes) return true;
  try {
    return document.querySelector('body').getAttribute('is-public') === 'true';
  } catch (error) {
    console.warn('[PES]: Could not check if code is executed from Public Environment', error);
    return false;
  }
};

/**
 * Provides the country code and language code extracted from locale="lc_CC" attribute within body tag. The country code is transformed to lowercase when the function detects it is executed from the public environment.
 *
 * @param isExecutedInBrowser This optional parameter is the method to check if the code is executed in the browser. When is not passed, the function will use a default callback.
 * @returns An object containing country code and language code.
 */
export const getLocaleFromBody = (isExecutedInBrowser?: () => boolean): Locale => {
  try {
    const locale = document.querySelector('body').getAttribute('locale');
    const [ languageCode, rawContryCode ] = locale.split('_');

    return {
      countryCode: isPublicEnvironment(isExecutedInBrowser)
        ? rawContryCode?.toLowerCase()
        : rawContryCode,
      languageCode,
    };
  } catch (error) {
    console.warn('[PES]: Could not get locale from body tag', error);
    return null;
  }
};

/**
 * Provides the country code and language code extracted from a URL passed as parameter. The country code is transformed to lowercase when the function detects it is executed from the public environment.
 *
 * @param url This mandatory parameter is the URL to extract the locale from. Examples: "https://www.se.com/es/es/product/tyreiuty/", "https://www.se.com//es/es/", "/es/es", "es/es", "https://www.schneider-electric.cn/zh/"
 * @param isExecutedInBrowser This optional parameter is the method to check if the code is executed in the browser. When is not passed, the function will use a default callback.
 * @returns An object containing country code and language code.
 */
export const getLocaleFromURL = (url: string, isExecutedInBrowser?: () => boolean): Locale => {
  try {
    const [ firstMatch, secondMatch ] = url.replace(/^https?:\/\/[^/]+/, '')
      .replace(/^\/(apc)?(asco)?\/?/, '')
      .split('/');

    if (firstMatch === 'zh' || secondMatch === 'zh') {
      return {
        countryCode: isPublicEnvironment(isExecutedInBrowser) ? 'cn' : 'CN',
        languageCode: firstMatch === 'zh' ? firstMatch : secondMatch,
      };
    }

    return {
      countryCode: secondMatch,
      languageCode: firstMatch,
    };
  } catch (error) {
    console.warn(`[PES]: Could not get locale from URL provided: ${url}`, error);
    return null;
  }
};
export const useReplatformedHMC = () => Boolean(window.isReplatformedHmc);
export const productCartPageVersion: CartVersion = window.cartVersion;

type BuildURLOptions = {
  host?: string;
  contextRoot?: string;
  countryCode?: string;
  languageCode?: string;
  endpoint: string;
};

export const buildURL = ({
  host,
  contextRoot,
  countryCode,
  languageCode,
  endpoint,
}: BuildURLOptions): string => {
  let path = endpoint;
  if (languageCode) {
    path = `/${languageCode}${path}`;
  }

  if (countryCode) {
    /**
     * countryCode for China is
     * - lowercase (cn) if China-specific short-domain url is used as a host: 'https://schneider-electric.'
     *   which should be concatenated with countryCode without '/'
     * - uppercase (CN) if RP urls are used - in this case path should start with '/' symbol
     * Other countries' paths start with '/' symbol
     */
    path = `${countryCode !== 'cn' ? '/' : ''}${countryCode}${path}`;
  }

  if (contextRoot) {
    path = `${contextRoot}${path}`;
  }

  if (host) {
    path = `${host}${path}`;
  }

  return path;
};

export const getSDLHeaderSelector = (): string => {
  if (isV3()) return SDL_REDESIGN_HEADER_SELECTOR;

  // @ts-ignore
  if (window.isApc) {
    return SDL_HEADER_SELECTOR_APC;
  }
  return SDL_HEADER_SELECTOR;
};

export const getHeaderHeight = () => (
  document.querySelector(getSDLHeaderSelector())?.clientHeight ?? 0
);

export const scrollToComponent = (
  el: HTMLElement,
  options: TScrollToComponentOptions = {},
): void => {
  const { behavior = scrollBehavior.AUTO, offset = TAB_BAR_HEIGHT } = options;

  // A quick and dirty solution to the issue with scrolling to a component on sites of pes-catalog consumers:
  //  - MySe
  //  - MySchneider
  // See https://jira.se.com/browse/PESV3-27234 for details.
  if (document.querySelector(getSDLHeaderSelector())) {
    //  If there is the SDL header (we are on se.com or apc.com), we can scroll to the component while taking into account the header and the tabbar heights.
    const top = el.getBoundingClientRect().y + window.scrollY - getHeaderHeight() - offset;

    window.scrollTo({ top, behavior });
  } else {
    // If there is no header, we don't even try to guess the height of a consumer's header and just scroll the component into view.
    //  Most likely, it will be partially covered by the header, but we decided not to care about that for now.
    el.scrollIntoView();
  }
};

export const consoleErrorMsg = (err, origin: string) => {
  console.error(`Error in ${origin}`, err);
};

export const mergeUrlWithHost = (host: string, url: string): string => {
  if (host?.endsWith(SLASH) && url.startsWith(SLASH)) {
    host.slice(0, host.length);
  }
  return `${host}${url}`;
};

export const hasHttpOrHttps = (host: string) => {
  return host.includes(`${Protocol.HTTP}://`) || host.includes(`${Protocol.HTTPS}://`);
};

export const validateHost = (host: string) => {
  return hasHttpOrHttps(host) ? host : '';
};

export const tryParse = (data: string) => {
  try {
    return JSON.parse(data);
  } catch (e) {
    return data;
  }
};

export const tryStringify = (data: any) => {
  try {
    return JSON.stringify(data);
  } catch (e) {
    return data;
  }
};

/**
 * Does object have any properties
 * @param {object} object
 */
export const isEmpty = (object = {}): boolean => Object.getOwnPropertyNames(object).length === 0;

export const parseJsonResponse = <T>(resp: Response): Promise<T> => resp.json();

/**
 * Parse string and return defaultValue param if string cannot be parsed.
 */
export const parseJSONString = (str: string, defaultValue: unknown | null): any => {
  try {
    return JSON.parse(str);
  } catch (e) {
    return defaultValue;
  }
};

export const isValidUrl = (url: string): boolean => {
  try {
    // eslint-disable-next-line no-new
    new URL(url);
  } catch {
    return false;
  }

  return true;
};

export const transformCamelCaseString = (string = '', delimeter = ''): string => {
  return string.replace(/([a-z0-9])([A-Z])/g, `$1${delimeter}$2`).toLowerCase();
};

/**
 * Merge target object and source object deeply, returning a target object with the elements from both target and source.
 * If an element at the same key is present for both target and source, the value from source will appear in the result.
 * Arrays are overwritten.
 * @param target
 * @param source
 */
// eslint-disable-next-line @typescript-eslint/default-param-last
export const deepMerge = (target: any = {}, source: any): any => {
  // eslint-disable-next-line no-restricted-syntax
  for (const key of Object.keys(source)) {
    if (Array.isArray(source[key])) {
      // eslint-disable-next-line no-param-reassign
      target[key] = [ ...source[key] ];
    } else if (source[key] instanceof Object) {
      Object.assign(source[key], deepMerge(target[key], source[key]));
    }
  }

  Object.assign(target || {}, source);
  return target;
};

export const getDataAttributes = (dataset: Record<string, string>): Record<string, string> => {
  if (!dataset) {
    return {};
  }

  return Object.keys(dataset).reduce<Record<string, string>>((acc, key) => {
    acc[`data-${transformCamelCaseString(key, '-')}`] = dataset[key];

    return acc;
  }, {});
};

export const roundHalf = (num: number): number => {
  return Math.round(num * 2) / 2;
};

export const convertToPx = (...values: number[]): string => {
  return values.map((val) => `${val}px`).join(' ');
};

export const encodeProductId = (id: string): string => encodeURIComponent(encodeURIComponent(id));

export const isTargetBlank = (target: string): boolean => target === '_blank';

export const isLinkTypeOnline = (type: string): boolean => type in ONLINE_CTA_TYPES;

export const sendEvent = (type: EEvent, data: unknown) => {
  const customEvent = new CustomEvent(type, {
    detail: data,
  });
  window.dispatchEvent(customEvent);
};

/**
 * Returns YouTube video ID from YouTube URL
 */
export const getYouTubeVideoIDFromURL = (youTubeURL: string) => {
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = youTubeURL.match(regExp);

  return match && match[7].length === 11 ? match[7] : '';
};

export const urlDecode = (encodedString: string) => decodeURIComponent(encodedString.replace(/\+/g, ' '));

export const enum MediaGalleryImageExtensions {
  SVG = '.svg',
}

/**
 * Checks if the URL is an SVG image
*/
export const isSVG = (url: string, isImage = false): boolean => {
  if (!url) return false;
  try {
    const parsedUrl = new URL(url);
    const params = new URLSearchParams(parsedUrl.search);
    const fileName = params.get('p_File_Name'); // This key can be found in https://download.schneider-electric.com/files?p_enDocType=Offer+Marketing+Illustration&p_File_Name=Infographics_XALD_K_XB4_5_V2.svg&p_Doc_Ref=Infographics_XALD_K_XB4_5_V2

    if (fileName && isImage) return fileName.endsWith(MediaGalleryImageExtensions.SVG) && isImage;

    return url.endsWith(MediaGalleryImageExtensions.SVG);
  } catch (error) {
    console.warn(error);
    return false;
  }
};

export const isUrl = (url: string): boolean => {
  if (!url) return false;

  try {
    new URL(url);
    return true;
  } catch (error) {
    // eslint-disable-next-line no-useless-escape
    if (/\/[^\/]+\.\w+$/.test(url)) { // this line of code is used to handle the case where the url is a relative path and ends with any image extensions'
      return true;
    }
    console.warn(error);
    return false;
  }
};

export const getCTAClickEventParams = (name, category, url, position): RangeCtaClickEventParams => {
  return {
    cta_location: position || '',
    cta_name: name || '',
    cta_category: category || '',
    cta_link: url || '',
  };
};

export function arrayToMap<T, K extends keyof T>(
  items: T[],
  key: K,
): Map<T[K], T> {
  const itemMap = new Map<T[K], T>();
  items.forEach((item) => {
    const mapKey = item[key];

    if (mapKey === undefined || mapKey === null) return;

    itemMap.set(mapKey, item);
  });

  return itemMap;
}

/**
 * Generate alphanumerical hash of specified length.
 */
export const makeId = (length = 5) => {
  let text = '';
  const possible = 'abcdefghijklmnopqrstuvwxyz0123456789';

  for (let i = 0; i < length; i += 1) {
    /* NOSONAR_BEGIN */
    text += possible.charAt(Math.floor(Math.random() * possible.length));
    /* NOSONAR_END */
  }

  return text;
};

export const COMPARATOR_LOCALE = document.documentElement.lang.replace('-', '_');

export const sleep = (ms = 0) => new Promise(
  (res) => {
    setTimeout(res, ms);
  },
);

export const allFinished = (
  promisesArray: Array<Promise<any>>,
) => Promise.all(promisesArray.map((promise) => promise.catch(noop)));

/**
 * Retrieves sessionId from storage if exists. Else generates new sessionId.
 */
export const getComparisonSessionId = () => {
  const seSessionId = 'COMPARISON_SESSION_ID';
  const apcSessionId = 'APC_COMPARISON_SESSION_ID';
  const COMPARISON_SESSION_ID = !isApc ? seSessionId : apcSessionId;

  let sessionId = localStorage.getItem(COMPARISON_SESSION_ID);

  if (!sessionId) {
    sessionId = makeId();
    localStorage.setItem(COMPARISON_SESSION_ID, sessionId);
  }

  return sessionId;
};

export const getComparatorThreshold = (): number | undefined => {
  const comparatorThreshold = parseInt(window.comparatorThreshold);
  return Number.isNaN(comparatorThreshold) ? undefined : comparatorThreshold;
};

export const getStorageComparatorName = (): string => {
  if (!isApc) {
    return `pesProductComparisonList_${COMPARATOR_LOCALE}`;
  }
  return 'comparisonList';
};

export const MAX_COMPARATOR_ITEMS = getComparatorThreshold();

const BACK_TO_RESULTS_LINK = 'pesBackLink';

const STORAGE_COMPARATOR_NAME = getStorageComparatorName();

const PRODUCT_IDS_SEPARATOR = ',';

class Comparator {
  constructor() {
    this.setListeners();

    if (!isApc) {
      this.setBackLink();
    }
  }

  setListeners() {
    window.addEventListener(EEvent.COMPARE_CONTROL_CLICK, this.handleCompareControlClick);
    window.addEventListener('storage', this.handleStorageChange);
  }

  setBackLink() {
    const currentURL = window.location.href;
    const isComparatorPage = currentURL.includes('/product-comparator/');

    if (!isComparatorPage) {
      window.localStorage.setItem(
        `${COMPARATOR_LOCALE}-${BACK_TO_RESULTS_LINK}`,
        currentURL,
      );
    }
  }

  getBackLink() {
    return window.localStorage.getItem(`${COMPARATOR_LOCALE}-${BACK_TO_RESULTS_LINK}`) || '';
  }

  getStoredProductIds(): string[] {
    const serializedList = localStorage.getItem(STORAGE_COMPARATOR_NAME) ?? '';
    if (!isApc) {
      return serializedList.split(PRODUCT_IDS_SEPARATOR).filter(Boolean);
    }
    let result = [];
    try {
      result = JSON.parse(serializedList);
    } finally {
      /* NOSONAR_BEGIN */
      // eslint-disable-next-line no-unsafe-finally
      return result;
      /* NOSONAR_END */
    }
  }

  store(productIds: string[]) {
    let serializedList;
    if (!isApc) {
      serializedList = productIds.join(PRODUCT_IDS_SEPARATOR);
    } else {
      serializedList = JSON.stringify(productIds);
    }
    localStorage.setItem(STORAGE_COMPARATOR_NAME, serializedList);
  }

  has(productId: string): boolean {
    return this.getStoredProductIds().includes(productId);
  }

  add(productId: string): void {
    const productIds = [ ...this.getStoredProductIds(), productId ];
    this.store(productIds);
  }

  remove(productId: string): void {
    const productIds = this.getStoredProductIds();
    this.store(productIds.filter((id) => productId !== id));
  }

  isFull() {
    if (MAX_COMPARATOR_ITEMS) {
      return this.getStoredProductIds().length >= MAX_COMPARATOR_ITEMS;
    }
    return false;
  }

  get value() {
    return {
      list: this.getStoredProductIds(),
      isFull: this.isFull(),
      productProp: 'repositoryId',
    };
  }

  notifySubscribers() {
    sendEvent(EEvent.COMPARATOR_PRODUCTS_CHANGE, this.value);
  }

  handleCompareControlClick = (event: CustomEvent<string | { repositoryId: string }>): void => {
    const productId = typeof event.detail === 'string' ? event.detail : event.detail.repositoryId;

    if (this.has(productId)) {
      this.remove(productId);
    } else if (!this.isFull()) {
      this.add(productId);
    } else {
      return;
    }
    this.notifySubscribers();
  };

  removeAll(): void {
    localStorage.removeItem(STORAGE_COMPARATOR_NAME);
    this.notifySubscribers();
  }

  handleStorageChange = (event: StorageEvent) => {
    if (event.key === STORAGE_COMPARATOR_NAME) {
      this.notifySubscribers();
    }
  };
}

export const comparator = new Comparator();

export const getComparatorUrl = (baseUrl: string) => {
  const productIds = comparator.getStoredProductIds().join('|');
  return `${baseUrl}/${getComparisonSessionId()}/${productIds}/`;
};

export const getSlottedContent = (slot: HTMLSlotElement): HTMLElement | null => {
  let slottedContentElement = null;

  if (slot) {
    while (!slottedContentElement || slottedContentElement.nodeName === 'SLOT') {
      if (slot.assignedElements().length) {
        if (slot.assignedElements()[0].nodeName === 'SLOT') {
          // eslint-disable-next-line no-param-reassign
          slot = slot.assignedElements()[0] as HTMLSlotElement;
        } else {
          // eslint-disable-next-line prefer-destructuring
          slottedContentElement = slot.assignedElements()[0];
        }
      } else {
        break;
      }
    }
  }

  return slottedContentElement;
};

export const getProductCtasLabels = ({
  labels = {},
}: {
  labels?: TProductCtasLabels;
  [key: string]: any;
}): TProductCtasLabels => {
  const {
    addToCartLabel,
    removeFromCartLabel,
    addToMyProductsLabel,
    removeFromMyProductsLabel,
  } = labels;
  return {
    addToCartLabel,
    removeFromCartLabel,
    addToMyProductsLabel,
    removeFromMyProductsLabel,
  };
};

export const addPageLoadDataInfoToAnalyticsEvent = (
  params = defaultPageLoadDataParams,
): Record<string, unknown> => {
  const pageLoadData = window?.pesAnalytics?.pageLoadData;

  return pageLoadData
    ? params.reduce((acc, curr) => {
      if (Object.getOwnPropertyDescriptor(pageLoadData, curr)) {
        acc[pageLoadDataToAnalytics[curr]] = pageLoadData[curr];
      }

      return acc;
    }, {})
    : {};
};
